import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Mail, MapPin, Send, ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import Footer from '../components/Footer';

export default function Contact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    // Simple mailto handoff (no SMTP needed)
    const subject = encodeURIComponent(`FM Flexi Pro inquiry from ${form.name}`);
    const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\n\n${form.message}`);
    window.location.href = `mailto:info@sgkapps.com?subject=${subject}&body=${body}`;
    setSent(true);
    toast.success('Email client opened. Send to info@sgkapps.com');
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0a0f1e]/70 border-b border-teal-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="p-2 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div className="leading-tight">
              <div className="text-lg font-bold">FM Flexi Pro<span className="text-teal-400 text-xs align-top">™</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">by SKG Apps</div>
            </div>
          </div>
          <Button data-testid="back-home-btn" variant="ghost" onClick={() => navigate('/')} className="text-slate-300 hover:text-white"><ArrowLeft className="w-4 h-4 mr-1" />Home</Button>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-500/10 border border-teal-500/20 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-teal-400" />
            <span className="text-xs font-medium text-teal-300">GET IN TOUCH</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">Let's run facilities <span className="text-teal-300">smarter, together.</span></h1>
          <p className="text-slate-400 max-w-2xl mx-auto">Tell us about your portfolio, and we'll show you how FM Flexi Pro can save your team hours every week.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-4">
            <Card className="bg-slate-900/50 border-slate-800 p-5">
              <Mail className="w-5 h-5 text-teal-400 mb-2" />
              <h3 className="font-semibold mb-1">Email</h3>
              <a data-testid="contact-email-link" href="mailto:info@sgkapps.com" className="text-teal-400 hover:underline text-sm">info@sgkapps.com</a>
            </Card>
            <Card className="bg-slate-900/50 border-slate-800 p-5">
              <MapPin className="w-5 h-5 text-teal-400 mb-2" />
              <h3 className="font-semibold mb-1">Office</h3>
              <p className="text-sm text-slate-400">SKG Apps · United Arab Emirates</p>
            </Card>
            <Card className="bg-gradient-to-br from-teal-900/40 to-cyan-900/40 border-teal-700/40 p-5">
              <Sparkles className="w-5 h-5 text-teal-300 mb-2" />
              <h3 className="font-semibold mb-1">Try the Demo</h3>
              <p className="text-xs text-slate-300 mb-3">Read-only access to a fully populated workspace.</p>
              <Button data-testid="contact-try-demo-btn" onClick={() => navigate('/')} size="sm" className="bg-white text-slate-900 hover:bg-slate-100">Open the App</Button>
            </Card>
          </div>

          <Card className="lg:col-span-2 bg-slate-900/50 border-slate-800 p-6 lg:p-8">
            {sent ? (
              <div className="text-center py-12">
                <div className="text-5xl mb-3">✉️</div>
                <h3 className="text-xl font-semibold mb-1">Email composed!</h3>
                <p className="text-slate-400 text-sm mb-4">Send it from your email client and our team will reply within 1 business day.</p>
                <Button data-testid="reset-form-btn" onClick={() => { setSent(false); setForm({ name: '', email: '', company: '', message: '' }); }} variant="outline">Send another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-300">Name</Label><Input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                  <div><Label className="text-slate-300">Work Email</Label><Input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                </div>
                <div><Label className="text-slate-300">Company / Property</Label><Input data-testid="contact-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                <div><Label className="text-slate-300">How can we help?</Label><Textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-slate-800 border-slate-700 text-white" placeholder="Tell us about your portfolio size, modules of interest, timelines..." /></div>
                <Button data-testid="contact-submit-btn" type="submit" className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white py-6 rounded-full font-semibold">
                  <Send className="w-4 h-4 mr-2" />Send Message to info@sgkapps.com
                </Button>
              </form>
            )}
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
}
